import { z } from 'zod';

const key = z.string().regex(/^[a-z][a-zA-Z0-9_-]{0,63}$/);
// Saved model recipes are structured data, shared by Apps, canvas cards and graph execution.
// Keep the accepted parameters explicit: a package cannot smuggle arbitrary execution options.
const modelFile = z.string().max(500).refine(v => !v.split(/[\\/]/).includes('..'), 'Invalid model file');
export const AppGenerationParamsSchema = z.object({
  steps: z.number().int().min(1).max(100).optional(), guidance: z.number().min(0).max(30).optional(),
  seed: z.number().int().min(-1).max(Number.MAX_SAFE_INTEGER).optional(), negative: z.string().max(12000).optional(),
  sampler: z.string().max(80).optional(), scheduler: z.string().max(80).optional(),
  denoise: z.number().min(0).max(1).optional(), shift: z.number().min(0).max(20).optional(),
  growMask: z.number().int().min(0).max(64).optional(), controlStrength: z.number().min(0).max(1.5).optional(),
  fps: z.number().int().min(1).max(60).optional(), duration: z.number().min(1).max(240).optional(),
  lyrics: z.string().max(12000).optional(), bpm: z.number().min(40).max(220).optional(),
  imageStrength: z.number().min(0).max(1).optional(), meshDetail: z.union([z.literal(1024), z.literal(1536), z.literal(2048)]).optional(),
  loras: z.array(z.object({ name: modelFile.min(1), weight: z.number().min(0).max(2) }).strict()).max(8).optional(),
  clipSkip: z.number().int().min(1).max(4).optional(), vae: modelFile.optional(),
  upscaleBase: modelFile.optional(),
  resolution: z.enum(['1k', '2k']).optional(), quality: z.enum(['auto', 'low', 'medium']).optional(),
}).strict();
export const APP_GENERATION_PARAM_KEYS = Object.keys(AppGenerationParamsSchema.shape);
export const AppGenerationSchema = z.object({
  provider: z.enum(['local', 'fal', 'replicate', 'xai']).default('local'), model: modelFile.default(''),
  params: AppGenerationParamsSchema.default({}), control: z.enum(['edges', 'depth', 'pose']).optional(),
}).strict();
export const AppValueSchema = z.union([z.string().max(12000), z.number().finite(), z.boolean(), AppGenerationSchema]);
// Presentation hints only change how a host renders an app; they never reach execution.
// Hosts ignore kinds they do not know and fall back to their default field for the type.
export type AppUiJson = string | number | boolean | null | AppUiJson[] | { [key: string]: AppUiJson };
const uiJson: z.ZodType<AppUiJson> = z.lazy(() => z.union([
  z.string().max(4000), z.number().finite(), z.boolean(), z.null(), z.array(uiJson).max(100), z.record(z.string().max(64), uiJson),
]));
const uiLabels = z.record(z.string().min(1).max(64), z.string().max(200)).refine(v => Object.keys(v).length <= 40, 'Too many labels.');
const uiOptions = z.record(z.string().min(1).max(64), uiJson).refine(v => JSON.stringify(v).length <= 16000, 'Presentation options are too large.');
const uiKind = z.string().regex(/^[a-z][a-z0-9-]{0,63}$/);
export const AppUiHintSchema = z.object({
  kind: uiKind.optional(), advanced: z.boolean().optional(), labels: uiLabels.optional(), options: uiOptions.optional(),
}).strict();
export const AppManifestUiSchema = z.object({
  kind: uiKind.optional(), labels: uiLabels.optional(), options: uiOptions.optional(),
  batch: z.boolean().optional(), cost: z.string().min(1).max(80).optional(),
}).strict();
export const AppPortSchema = z.object({
  id: key, label: z.string().min(1).max(80),
  type: z.enum(['image', 'text', 'video', 'audio', 'mesh']), required: z.boolean().default(true),
  ui: AppUiHintSchema.optional(),
}).strict();
export const AppControlSchema = z.object({
  id: key, label: z.string().min(1).max(80),
  type: z.enum(['text', 'textarea', 'number', 'select', 'boolean', 'color', 'generation']),
  default: AppValueSchema, min: z.number().finite().optional(), max: z.number().finite().optional(),
  step: z.number().positive().optional(), options: z.array(z.string().max(120)).min(1).max(40).optional(),
  ui: AppUiHintSchema.optional(),
}).strict().superRefine((c, ctx) => {
  if (c.type === 'number' && (typeof c.default !== 'number' || c.min === undefined || c.max === undefined || c.min > c.max || c.default < c.min || c.default > c.max)) ctx.addIssue({ code: 'custom', message: 'Number controls require bounded numeric defaults.' });
  if (c.type === 'select' && (typeof c.default !== 'string' || !c.options?.includes(c.default))) ctx.addIssue({ code: 'custom', message: 'Select defaults must appear in options.' });
  if (c.type === 'boolean' && typeof c.default !== 'boolean') ctx.addIssue({ code: 'custom', message: 'Boolean default required.' });
  if (['text', 'textarea', 'color'].includes(c.type) && typeof c.default !== 'string') ctx.addIssue({ code: 'custom', message: 'Text default required.' });
  if (c.type === 'generation' && (c.id !== 'generation' || !AppGenerationSchema.safeParse(c.default).success)) ctx.addIssue({ code: 'custom', message: 'Generation controls require a model recipe and the ID generation.' });
});
// Capabilities are approved engine operations, never uploaded JavaScript or arbitrary URLs.
export const AppOperationSchema = z.enum([
  'edit.adjust', 'edit.crop', 'edit.split-grid', 'edit.transform', 'compose.resize', 'compose.text', 'compose.composite',
  'edit.instruct', 'edit.variation', 'edit.inpaint', 'edit.erase', 'edit.segment', 'edit.detail',
  'edit.expand', 'edit.upscale', 'edit.rembg', 'edit.vectorize', 'edit.interpolate',
  // Cut-out care, instant: clean the alpha, match the tone, a shadow under the feet.
  'edit.alpha', 'edit.match', 'edit.shadow',
  // Adjust modes and the audit, instant: grade to the board's light, inspect frames and flag them.
  'edit.grade', 'edit.inspect',
  // A cut by a selection, instant: keep it or cut it out.
  'edit.cutout',
  // A cut-out painted again alone on a transparent background (fal's gpt-image-2), fitted back in its frame.
  'edit.bgfree',
  'gen.image', 'gen.video', 'gen.sound', 'gen.3d', 'text.describe', 'text.enhance',
  // A hosted model tool (lipsync, relight, transcribe…): Genie credits, or the user's fal key.
  'tool.run',
  // Data Dune pigment render: runs in the desktop window (WebGPU), never in the engine.
  'render.pigment',
]);
export const CanvasAppManifestSchema = z.object({
  schemaVersion: z.literal(1), id: z.string().regex(/^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*){2,}$/).max(160),
  version: z.string().regex(/^\d+\.\d+\.\d+$/), name: z.string().min(1).max(80),
  description: z.string().min(1).max(500), category: z.enum(['Images', 'Video', '3D', 'Audio', 'Text', 'Utilities']),
  inputs: z.array(AppPortSchema).max(6), output: AppPortSchema,
  controls: z.array(AppControlSchema).max(20),
  execution: z.object({ operation: AppOperationSchema, defaults: z.record(key, AppValueSchema).default({}) }).strict(),
  ui: AppManifestUiSchema.optional(),
}).strict().superRefine((m, ctx) => {
  if (m.inputs.some(p => p.id === 'operation')) ctx.addIssue({ code: 'custom', message: 'The input ID operation is reserved.' });
  for (const items of [m.inputs, m.controls]) if (new Set(items.map(i => i.id)).size !== items.length) ctx.addIssue({ code: 'custom', message: 'Input and control IDs must be unique within their groups.' });
});
export const CanvasAppStateSchema = z.object({
  appId: z.string().max(160), version: z.string().max(40),
  values: z.record(key, AppValueSchema).default({}),
  inputs: z.record(key, z.string().min(1).max(160)).default({}),
  runId: z.string().max(160).optional(),
}).strict();
export type CanvasAppManifest = z.infer<typeof CanvasAppManifestSchema>;
export type CanvasAppState = z.infer<typeof CanvasAppStateSchema>;
export type AppControl = z.infer<typeof AppControlSchema>;
export type AppPort = z.infer<typeof AppPortSchema>;
export type AppValue = z.infer<typeof AppValueSchema>;
export type AppGeneration = z.infer<typeof AppGenerationSchema>;
export type AppUiHint = z.infer<typeof AppUiHintSchema>;
export type AppManifestUi = z.infer<typeof AppManifestUiSchema>;
